import { makeAutoObservable, runInAction } from "mobx";
import type { ApiClient } from "../platform/api-client";

/**
 * Shell notification inbox (M23): polled while the app is open, rendered as
 * the header bell. Content is already authorization-filtered and, where
 * required, redacted server-side; the client only displays and acknowledges.
 */
export interface ShellNotification {
  readonly deliveryId: string;
  readonly notificationId: string;
  readonly typeCode: string;
  readonly title: string;
  readonly body: string;
  readonly contextPath: string | null;
  readonly redacted: boolean;
  readonly deliveredAt: string;
  readonly readAt: string | null;
  readonly acknowledgementRequired: boolean;
  readonly acknowledgedAt: string | null;
  readonly state: string;
  readonly version: number;
}

export class NotificationStore {
  items: readonly ShellNotification[] = [];
  loaded = false;

  constructor(private readonly api: ApiClient) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get unreadCount(): number {
    return this.items.filter((item) => item.readAt === null).length;
  }

  /** Begins polling; returns the disposer for the caller's effect cleanup. */
  start(intervalMs = 30_000): () => void {
    void this.refresh();
    const timer = window.setInterval(() => {
      void this.refresh();
    }, intervalMs);
    return () => {
      window.clearInterval(timer);
    };
  }

  async refresh(signal?: AbortSignal): Promise<void> {
    try {
      const response = await this.api.get<{ readonly items: readonly ShellNotification[] }>(
        "/v1/notifications?limit=20",
        signal,
      );
      runInAction(() => {
        this.items = response.items;
        this.loaded = true;
      });
    } catch {
      // The bell quietly keeps its last state; the next poll retries.
    }
  }

  async markRead(deliveryId: string): Promise<void> {
    const item = this.items.find((entry) => entry.deliveryId === deliveryId);
    if (item === undefined || item.readAt !== null) return;
    try {
      await this.api.post<unknown>(
        `/v1/notifications/${encodeURIComponent(deliveryId)}/read`,
        undefined,
        { ifMatch: item.version },
      );
      runInAction(() => {
        this.items = this.items.map((entry) => entry.deliveryId === deliveryId
          ? { ...entry, readAt: new Date().toISOString(), version: entry.version + 1 }
          : entry);
      });
    } catch {
      await this.refresh();
    }
  }

  async acknowledge(deliveryId: string): Promise<void> {
    const item = this.items.find((entry) => entry.deliveryId === deliveryId);
    if (item === undefined || item.acknowledgedAt !== null) return;
    try {
      await this.api.post<unknown>(
        `/v1/notifications/${encodeURIComponent(deliveryId)}/acknowledge`,
        undefined,
        { ifMatch: item.version },
      );
      runInAction(() => {
        this.items = this.items.map((entry) => entry.deliveryId === deliveryId
          ? {
              ...entry,
              acknowledgedAt: new Date().toISOString(),
              state: "ACKNOWLEDGED",
              version: entry.version + 1,
            }
          : entry);
      });
    } catch {
      await this.refresh();
    }
  }

  async markAllRead(): Promise<void> {
    for (const item of this.items.filter((entry) => entry.readAt === null)) {
      await this.markRead(item.deliveryId);
    }
  }
}
